/**
 * SendDigestModal
 *
 * Compose & send a portfolio digest via email.
 *
 * Two audience modes:
 * - "Team digest"  — pick agency staff recipients, body is the portfolio-wide
 *   rollup (at-risk brands, scheduling cliffs, top performers).
 * - "Brand digest" — pick one brand, body is that brand's data
 *   (latest status update, runway, top performer). Recipients default to the
 *   brand lead but can be expanded to multiple staff.
 *
 * Both modes call the same POST /api/agency/portfolio/send-digest endpoint.
 * The preview comes from GET /api/agency/portfolio/digest-preview: the SAME server
 * builder the email uses, so the preview never drifts from what is sent.
 */

import { usePortfolioLocks } from '@/hooks/usePortfolioLocks';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { showToast } from '@/components/ui/Toast';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { portfolioService } from '@/services/portfolio.service';
import { usePortfolioClients, useAgencyStaff, useDigestPreview } from '@/hooks/usePortfolio';
import type { PortfolioClient, AgencyStaffMember } from '@/data/portfolio.types';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { avatarTextClass } from '@/utils/avatarContrast';
import type { DigestBrandRow } from '@/services/portfolio.service';
import { lockBodyScroll } from '@/hooks/useBodyScrollLock';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SendDigestModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional: pre-select a single brand (opens in brand-digest mode for that team) */
  initialBrandId?: number;
}

type DigestMode = 'team' | 'brand';

// ── Component ─────────────────────────────────────────────────────────────────

export const SendDigestModal = ({ isOpen, onClose, initialBrandId }: SendDigestModalProps) => {
  const { t } = useTranslation('agency');
  const { isLocked } = usePortfolioLocks();

  // ── Live data ──
  const { data: portfolioData } = usePortfolioClients();
  const portfolioClients = useMemo<PortfolioClient[]>(
    () => portfolioData?.clients ?? [],
    [portfolioData],
  );
  const { data: agencyStaff } = useAgencyStaff();
  const staffList = useMemo<AgencyStaffMember[]>(() => agencyStaff ?? [], [agencyStaff]);

  // ── Mode + selection state ──
  const [mode, setMode] = useState<DigestMode>(initialBrandId ? 'brand' : 'team');
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(initialBrandId ?? null);
  const [selectedStaffIds, setSelectedStaffIds] = useState<number[]>([]);
  const [extraEmail, setExtraEmail] = useState('');
  const [extraEmails, setExtraEmails] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  // Snapshot of the auto-populated default recipient(s) (e.g. the brand lead
  // auto-selected below) — used to distinguish "still the default" from
  // "user actually changed something" for the unsaved-work guard.
  const [initialSelectedStaffIds, setInitialSelectedStaffIds] = useState<number[]>([]);

  // ── Reset when opened ──
  useEffect(() => {
    if (!isOpen) return;
    setMode(initialBrandId ? 'brand' : 'team');
    setSelectedBrandId(initialBrandId ?? null);
    setSelectedStaffIds([]);
    setInitialSelectedStaffIds([]);
    setExtraEmail('');
    setExtraEmails([]);
    setSaving(false);
  }, [isOpen, initialBrandId]);

  // ── Auto-pick brand lead when entering brand mode ──
  const selectedBrand = useMemo(
    () => portfolioClients.find((c) => c.id === selectedBrandId) ?? null,
    [portfolioClients, selectedBrandId],
  );

  useEffect(() => {
    if (mode === 'brand' && selectedBrand?.ownerId && selectedStaffIds.length === 0) {
      setSelectedStaffIds([selectedBrand.ownerId]);
      setInitialSelectedStaffIds([selectedBrand.ownerId]);
    }
  }, [mode, selectedBrand, selectedStaffIds.length]);

  // ── Server-built digest preview (same builder as the email) ──
  const previewQuery = useDigestPreview(
    mode,
    selectedBrandId,
    isOpen && (mode === 'team' || selectedBrandId !== null),
  );
  const previewDigest = previewQuery.data?.digest ?? null;
  const teamPreview = previewDigest?.type === 'team' && mode === 'team' ? previewDigest : null;
  const brandPreview = previewDigest?.type === 'brand' && mode === 'brand' ? previewDigest : null;
  // Avatar only (display); the rows themselves come from the server payload.
  const avatarOf = (row: DigestBrandRow): PortfolioClient | null =>
    portfolioClients.find((c) => c.id === row.brandId) ?? null;

  // ── Subject: built ONLY by the server (same builder as the email); the modal shows what it returns ──
  const subject = previewQuery.data?.subject ?? '';

  // ── ESC key + body scroll lock ──
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) handleClose();
    };
    document.addEventListener('keydown', handler);
    const unlockScroll = lockBodyScroll();
    return () => {
      document.removeEventListener('keydown', handler);
      unlockScroll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    setSaving(false);
    onClose();
  };

  const toggleStaff = (id: number) => {
    setSelectedStaffIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleExtraEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && extraEmail.trim()) {
      e.preventDefault();
      const email = extraEmail.trim().replace(/,$/, '').toLowerCase();
      const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (isValid && !extraEmails.includes(email)) {
        setExtraEmails((prev) => [...prev, email]);
      }
      setExtraEmail('');
    } else if (e.key === 'Backspace' && !extraEmail && extraEmails.length > 0) {
      setExtraEmails((prev) => prev.slice(0, -1));
    }
  };

  const removeExtraEmail = (email: string) => {
    setExtraEmails((prev) => prev.filter((e) => e !== email));
  };

  // ── Resolved recipient emails ──
  const recipientEmails = useMemo<string[]>(() => {
    const fromStaff = selectedStaffIds
      .map((id) => staffList.find((s) => s.id === id)?.email)
      .filter((e): e is string => typeof e === 'string' && e.length > 0);
    return Array.from(new Set([...fromStaff, ...extraEmails]));
  }, [selectedStaffIds, staffList, extraEmails]);

  const canSend =
    recipientEmails.length > 0 && (mode === 'team' || selectedBrandId !== null);

  // ── Submit ──
  const handleSend = async () => {
    if (!canSend) return;
    setSaving(true);
    try {
      // Server builds the digest from fresh portfolio data — we just tell it
      // who to send to and which audience mode to use. The preview below is built by the same server builder.
      const result = await portfolioService.sendDigest({
        recipients: recipientEmails,
        audience: mode,
        teamId: mode === 'brand' && selectedBrandId !== null ? selectedBrandId : undefined,
      });
      // Surface real email-send outcome rather than just "queued". If any
      // recipient failed (e.g. invalid address, Brevo rate limit), show a
      // mixed-success warning so the user knows to re-check.
      if (result.failedCount > 0) {
        showToast(
          t('fab_send_digest_toast_partial', {
            sent: result.sentCount,
            total: result.queuedCount,
            failed: result.failedCount,
            defaultValue: `Digest sent to ${result.sentCount} of ${result.queuedCount} recipients`,
          }),
          'warning',
        );
      } else if (result.skippedCount > 0) {
        // Suppressed / blocked recipients got nothing: say so instead of counting them as sent.
        showToast(
          t('fab_send_digest_toast_skipped', {
            count: result.sentCount,
            skipped: result.skippedCount,
            defaultValue_one: 'Sent to {{count}}, {{skipped}} skipped',
            defaultValue_other: 'Sent to {{count}}, {{skipped}} skipped',
          }),
          'warning',
        );
      } else {
        showToast(
          t('fab_send_digest_toast_success', {
            count: result.sentCount,
            defaultValue: `Digest sent to ${result.sentCount} recipient${result.sentCount !== 1 ? 's' : ''}`,
          }),
          'success',
        );
      }
      handleClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      showToast(
        t('fab_send_digest_toast_error', { msg, defaultValue: `Could not send digest: ${msg}` }),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  // Unsaved-work guard: dirty while open and the user has actually changed
  // something — either diverged from the auto-populated default recipient
  // (e.g. the brand lead auto-selected on open, see effect above) or started
  // typing an extra email. Comparing against `initialSelectedStaffIds` (not
  // just `> 0`) avoids false-positiving on the auto-select itself.
  const staffSelectionDiffersFromInitial =
    selectedStaffIds.length !== initialSelectedStaffIds.length ||
    !selectedStaffIds.every((id) => initialSelectedStaffIds.includes(id));

  useUnsavedGuard(
    isOpen &&
      (staffSelectionDiffersFromInitial ||
        extraEmails.length > 0 ||
        extraEmail.trim().length > 0),
    'send-digest-modal'
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed left-0 right-0 top-0 bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:inset-0 z-50 flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="send-digest-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className="
          relative bg-white flex flex-col shadow-2xl
          w-full h-full rounded-t-2xl rounded-b-none
          sm:h-auto sm:max-h-[92dvh] sm:w-[calc(100vw-2rem)] sm:max-w-2xl sm:mx-0 sm:rounded-2xl
          overflow-hidden
        "
      >
        {/* Drag handle (mobile) */}
        <div className="sm:hidden flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" aria-hidden="true" />
        </div>

        {/* ── Sticky Header ── */}
        <div className="relative shrink-0 border-b border-gray-900/10 pl-5 pr-14 py-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-medium mb-1">
            {t('fab_digest_overline', { defaultValue: 'Portfolio digest' })}
          </p>
          <div>
            <h2
              id="send-digest-title"
              className="font-wagner text-2xl leading-tight tracking-tight text-gray-900"
            >
              {t('fab_digest_title', { defaultValue: 'Monday brief' })}
            </h2>
            <button
              type="button"
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors outline-none border border-transparent focus-visible:border-primary-600"
              onClick={handleClose}
              aria-label={t('modal_close', { defaultValue: 'Close' })}
            >
              <i className="hm hm-cross" style={{ fontSize: 15 }} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* ── Scrollable Body ── */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

          {/* ── Audience toggle ── */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {t('fab_digest_audience_label', { defaultValue: 'Audience' })}
            </p>
            <div
              className="grid grid-cols-2 gap-0.5 p-0.5 bg-gray-100 rounded-lg"
              role="tablist"
              aria-label={t('fab_digest_audience_label', { defaultValue: 'Audience' })}
            >
              {(['team', 'brand'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  role="tab"
                  aria-selected={mode === m}
                  onClick={() => setMode(m)}
                  className={`flex items-center justify-center gap-1.5 h-9 rounded-md text-xs font-semibold transition-colors duration-150 outline-none border border-transparent focus-visible:border-primary-600 ${
                    mode === m
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i
                    className={`hm ${m === 'team' ? 'hm-group' : 'hm-board'}`}
                    style={{ fontSize: 13 }}
                    aria-hidden="true"
                  />
                  {m === 'team'
                    ? t('fab_digest_audience_team', { defaultValue: 'Team digest' })
                    : t('fab_digest_audience_brand', { defaultValue: 'Brand digest' })}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5">
              {mode === 'team'
                ? t('fab_digest_audience_team_hint', {
                    defaultValue: 'Portfolio-wide rollup for your team — one email, many brands.',
                  })
                : t('fab_digest_audience_brand_hint', {
                    defaultValue: 'Single-brand recap — defaults to the brand lead.',
                  })}
            </p>
          </div>

          {/* ── Brand picker (brand mode only) ── */}
          {mode === 'brand' && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {t('fab_digest_brand_label', { defaultValue: 'Brand' })}
              </p>
              <Select
                value={selectedBrandId !== null ? String(selectedBrandId) : ''}
                onValueChange={(v) => setSelectedBrandId(v ? parseInt(v, 10) : null)}
                placeholder={t('fab_digest_brand_placeholder', { defaultValue: 'Select a brand…' })}
                options={portfolioClients.map((c) => ({
                  value: String(c.id),
                  label: c.ownerName ? `${c.name} · ${c.ownerName}` : c.name,
                }))}
              />
            </div>
          )}

          {/* ── Recipients: staff multi-select ── */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {t('fab_digest_recipients_label', { defaultValue: 'Recipients' })}
            </p>
            {staffList.length === 0 ? (
              <p className="text-xs text-gray-400 italic">
                {t('fab_digest_no_staff', { defaultValue: 'No portfolio staff available.' })}
              </p>
            ) : (
              <div className="rounded-lg border border-gray-200 max-h-44 overflow-y-auto divide-y divide-gray-100">
                {staffList.map((s) => {
                  const checked = selectedStaffIds.includes(s.id);
                  return (
                    // <label> (not <button>): the Radix Checkbox is itself a button, so a button row
                    // would nest buttons. Clicking anywhere on the label activates the checkbox.
                    <label
                      key={s.id}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors text-left cursor-pointer focus-within:bg-gray-50"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleStaff(s.id)}
                        aria-label={s.name}
                        inline
                      />
                      <span
                        className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${avatarTextClass(s.avatarColor)} shrink-0 ring-1 ring-gray-200 ${s.avatarColor}`}
                        aria-hidden="true"
                      >
                        {s.initials}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate leading-tight">
                          {s.name}
                          {mode === 'brand' &&
                            selectedBrand?.ownerId === s.id && (
                              <span className="ml-1.5 text-[10px] uppercase tracking-wide text-primary-600 font-semibold">
                                {t('fab_digest_brand_lead_tag', { defaultValue: 'Brand lead' })}
                              </span>
                            )}
                        </p>
                        <p className="text-[11px] text-gray-400 truncate">{s.email ?? s.role}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Extra emails (free-form, optional) */}
            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium mb-1.5">
                {t('fab_digest_extra_emails_label', { defaultValue: 'Add other emails' })}
              </p>
              <div className="flex flex-wrap items-center gap-1.5 min-h-[38px] px-3 py-1.5 border border-gray-200 rounded-lg bg-white focus-within:border-primary-600">
                {extraEmails.map((email) => (
                  <span
                    key={email}
                    className="flex items-center gap-1 px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium"
                  >
                    {email}
                    <button
                      type="button"
                      className="ml-0.5 text-primary-500 hover:text-primary-700 outline-none rounded-full border border-transparent focus-visible:border-primary-600"
                      onClick={() => removeExtraEmail(email)}
                      aria-label={t('fab_digest_remove_email_aria', { email })}
                    >
                      <i className="hm hm-cross" style={{ fontSize: 9 }} aria-hidden="true" />
                    </button>
                  </span>
                ))}
                <input
                  type="email"
                  value={extraEmail}
                  onChange={(e) => setExtraEmail(e.target.value)}
                  onKeyDown={handleExtraEmailKeyDown}
                  placeholder={
                    extraEmails.length === 0
                      ? t('fab_digest_extra_emails_placeholder', {
                          defaultValue: 'name@example.com',
                        })
                      : ''
                  }
                  className="flex-1 min-w-[120px] text-sm text-gray-700 outline-none bg-transparent placeholder:text-gray-400"
                  aria-label={t('fab_digest_extra_emails_label', {
                    defaultValue: 'Add other emails',
                  })}
                />
              </div>
              <p className="text-[10px] text-gray-400 mt-1">
                {t('fab_digest_extra_emails_hint', {
                  defaultValue: 'Press Enter or comma to add. Useful for clients or external stakeholders.',
                })}
              </p>
            </div>
          </div>

          {/* ── Email preview ── */}
          <div className="rounded-2xl border border-gray-200 bg-gray-50 overflow-hidden">
            <div className="bg-white border-b border-gray-100 px-4 py-3">
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium">
                {t('fab_digest_preview_label', { defaultValue: 'Email preview' })}
              </p>
              {previewQuery.isLoading && !subject ? (
                <div
                  className="mt-1 h-4 w-2/3 animate-pulse rounded bg-gray-200"
                  role="status"
                  aria-busy="true"
                  aria-label={t('fab_digest_preview_loading', { defaultValue: 'Loading preview' })}
                />
              ) : (
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{subject}</p>
              )}
            </div>

            <div className="px-4 py-4 space-y-4">
              {previewQuery.isLoading && (mode === 'team' || selectedBrandId !== null) && (
                <div className="flex justify-center py-6">
                  <LoadingSpinner size="sm" />
                </div>
              )}

              {previewQuery.isError && (
                <p className="text-xs text-coral-700 text-center py-4" role="alert">
                  {t('fab_digest_preview_error', { defaultValue: 'Could not load the preview.' })}
                </p>
              )}

              {teamPreview && (
                <>
                  {/* Summary block */}
                  <div className="bg-white rounded-xl border border-gray-100 p-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      {t('fab_digest_section_summary', { defaultValue: 'Portfolio summary' })}
                    </p>
                    <div className="flex flex-wrap gap-4">
                      {teamPreview.kpis.map((kpi) => (
                        <div key={kpi.key ?? kpi.label} className="text-center">
                          <p className="text-2xl font-bold text-gray-900 tabular-nums">{kpi.value}</p>
                          <p className="text-xs text-gray-400">
                            {kpi.key === 'brands'
                              ? t('fab_digest_brands_total', { defaultValue: 'brands total' })
                              : kpi.key === 'on_track'
                              ? t('fab_digest_on_track', { defaultValue: 'on track' })
                              : kpi.key === 'watch'
                              ? t('fab_digest_watch', { defaultValue: 'watch' })
                              : kpi.key === 'at_risk'
                              ? t('fab_digest_at_risk', { defaultValue: 'at risk' })
                              : kpi.label}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {teamPreview.atRisk.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-coral-700 uppercase tracking-wide mb-2">
                        {t('fab_digest_section_at_risk', { defaultValue: 'At-risk this week' })} (
                        {teamPreview.atRisk.length})
                      </p>
                      <div className="space-y-2">
                        {teamPreview.atRisk.map((row) => {
                          const client = avatarOf(row);
                          return (
                            <div
                              key={row.brandId}
                              className="flex items-start gap-3 bg-white rounded-xl border border-coral-100 px-3 py-2.5"
                            >
                              {client && (
                                <div
                                  className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold ${avatarTextClass(client.avatarColor)} text-xs shrink-0 ${client.avatarColor}`}
                                  aria-hidden="true"
                                >
                                  {client.avatarInitial}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-900">{row.name}</p>
                                <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{row.detail}</p>
                              </div>
                              <span className="text-xs px-2 py-0.5 rounded-full font-semibold shrink-0 bg-coral-50 text-coral-700">
                                {row.tier === 'off_track'
                                  ? t('fab_digest_off_track', { defaultValue: 'Off track' })
                                  : t('fab_digest_at_risk_label', { defaultValue: 'At risk' })}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {teamPreview.cliffs.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-coral-700 uppercase tracking-wide mb-2">
                        {t('fab_digest_section_cliffs', { defaultValue: 'Low pipeline' })}
                      </p>
                      <div className="space-y-1.5">
                        {teamPreview.cliffs.map((row) => {
                          const client = avatarOf(row);
                          return (
                            <div
                              key={row.brandId}
                              className="flex items-center gap-2.5 bg-white rounded-lg border border-peach/60 px-3 py-2"
                            >
                              {client && (
                                <div
                                  className={`w-6 h-6 rounded-md flex items-center justify-center font-bold ${avatarTextClass(client.avatarColor)} text-[10px] shrink-0 ${client.avatarColor}`}
                                  aria-hidden="true"
                                >
                                  {client.avatarInitial}
                                </div>
                              )}
                              <p className="text-sm font-medium text-gray-800 flex-1">{row.name}</p>
                              <span className="text-xs text-coral-700 font-semibold">{row.detail}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {teamPreview.topPerformers.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-primary-700 uppercase tracking-wide mb-2">
                        {t('fab_digest_section_top', { defaultValue: 'Award-worthy moments' })}
                      </p>
                      <div className="space-y-1.5">
                        {teamPreview.topPerformers.map((row) => {
                          const client = avatarOf(row);
                          return (
                            <div
                              key={row.brandId}
                              className="flex items-center gap-2.5 bg-white rounded-lg border border-primary-100 px-3 py-2"
                            >
                              {client && (
                                <div
                                  className={`w-6 h-6 rounded-md flex items-center justify-center font-bold ${avatarTextClass(client.avatarColor)} text-[10px] shrink-0 ${client.avatarColor}`}
                                  aria-hidden="true"
                                >
                                  {client.avatarInitial}
                                </div>
                              )}
                              <p className="text-sm font-medium text-gray-800 flex-1">{row.name}</p>
                              <span className="text-xs text-primary-600 font-semibold tabular-nums">{row.metric}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}

              {brandPreview && (
                <>
                  {/* Brand header */}
                  <div className="bg-white rounded-xl border border-gray-100 p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${avatarTextClass(selectedBrand?.avatarColor ?? '')} text-base shrink-0 ${selectedBrand?.avatarColor ?? ''}`}
                        aria-hidden="true"
                      >
                        {selectedBrand?.avatarInitial}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-wagner text-lg leading-tight text-gray-900">
                          {brandPreview.brandName}
                        </p>
                        <p className="text-xs text-gray-400 capitalize">
                          {brandPreview.tier.replace('_', ' ')}
                          {brandPreview.ownerName ? ` · ${brandPreview.ownerName}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100">
                      <div className="text-center">
                        <p className="text-xl font-bold text-gray-900 tabular-nums">
                          {!isLocked('portfolio_runway') && t('runway_bar_days_short', { days: brandPreview.coverDays })}
                        </p>
                        <p className="text-[10px] uppercase tracking-wide text-gray-400">
                          {t('fab_digest_brand_runway', { defaultValue: 'Pipeline' })}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xl font-bold text-gray-900 tabular-nums">
                          {brandPreview.cadenceDescription}
                        </p>
                        <p className="text-[10px] uppercase tracking-wide text-gray-400">
                          {t('fab_digest_brand_cadence', { defaultValue: 'Cadence' })}
                        </p>
                      </div>
                      <div className="text-center">
                        <p
                          className={`text-xl font-bold tabular-nums ${
                            brandPreview.trendPct >= 0
                              ? 'text-primary-600'
                              : 'text-coral-600'
                          }`}
                        >
                          {brandPreview.trendPct > 0 ? '+' : ''}
                          {Math.round(brandPreview.trendPct)}%
                        </p>
                        <p className="text-[10px] uppercase tracking-wide text-gray-400">
                          {t('fab_digest_brand_trend', { defaultValue: 'Trend' })}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Status update */}
                  {brandPreview.tldr ? (
                    <div className="bg-white rounded-xl border border-gray-100 p-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        {t('fab_digest_brand_status', { defaultValue: 'This week' })}
                      </p>
                      <div className="flex items-start gap-3 mb-2">
                        <span className="text-2xl leading-none shrink-0" aria-hidden="true">
                          {brandPreview.vibeEmoji}
                        </span>
                        <p className="text-sm text-gray-800 leading-snug font-medium">
                          {brandPreview.tldr}
                        </p>
                      </div>
                      {brandPreview.wins && (
                        <div className="mt-2">
                          <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">
                            {t('fab_digest_brand_wins', { defaultValue: 'Wins' })}
                          </p>
                          <p className="text-xs text-gray-700 mt-0.5 whitespace-pre-line">
                            {brandPreview.wins}
                          </p>
                        </div>
                      )}
                      {brandPreview.nextFocus && (
                        <div className="mt-2">
                          <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">
                            {t('fab_digest_brand_next', { defaultValue: 'Next focus' })}
                          </p>
                          <p className="text-xs text-gray-700 mt-0.5 whitespace-pre-line">
                            {brandPreview.nextFocus}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
                      <p className="text-xs text-gray-400 italic">
                        {t('fab_digest_brand_no_update', {
                          defaultValue: 'No status update yet for this brand.',
                        })}
                      </p>
                    </div>
                  )}
                </>
              )}

              {mode === 'brand' && selectedBrandId === null && (
                <div className="bg-white rounded-xl border border-gray-100 p-6 text-center">
                  <p className="text-xs text-gray-400 italic">
                    {t('fab_digest_brand_pick_first', {
                      defaultValue: 'Pick a brand above to preview its recap.',
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Sticky Footer ── */}
        <div className="shrink-0 border-t border-gray-100 bg-white px-5 py-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
          <div className="flex items-center gap-2 justify-end">
            <CancelButton onClick={handleClose} tooltip={t('fab_cancel', { defaultValue: 'Cancel' })} />
            <Button
              variant="primary"
              size="md"
              disabled={!canSend}
              loading={saving}
              onClick={() => {
                void handleSend();
              }}
            >
              <i className="hm hm-paper-plane mr-1.5" style={{ fontSize: 14 }} aria-hidden="true" />
              {recipientEmails.length > 0
                ? t('fab_digest_submit', {
                    count: recipientEmails.length,
                    defaultValue_one: 'Send to {{count}} recipient',
                    defaultValue_other: 'Send to {{count}} recipients',
                  })
                : t('fab_send_digest', { defaultValue: 'Send digest' })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendDigestModal;
